/** Copilot Studio governance tools backed by the official Power Platform API. */

import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import {
  COPILOT_ADMIN_PERMISSION,
  type CopilotAdminApi,
  type CopilotConnectorConsentBypassStatus,
  type CopilotOwnerReassignment,
  type CopilotQuarantineStatus,
} from "../api/copilot-admin-api.js";
import { ConfirmationRequiredError } from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

const GUID_JSON_PATTERN =
  "^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$";

const guidSchema = (field: string) => z.string().regex(GUID_PATTERN, `${field} must be a GUID`);

const environmentSchema = z
  .string()
  .min(1)
  .max(128)
  .optional()
  .describe("Environment GUID/name. Defaults to the session's current environment.");

const agentInputShape = {
  agentId: guidSchema("agentId"),
  environment: environmentSchema,
};

export const getCopilotAgentQuarantineInputSchema = z.object(agentInputShape).strict();

export const setCopilotAgentQuarantineInputSchema = z
  .object({
    ...agentInputShape,
    quarantined: z.boolean(),
    confirmChange: z.boolean().optional(),
  })
  .strict();

export const getCopilotAgentConnectorConsentBypassInputSchema = z.object(agentInputShape).strict();

export const setCopilotAgentConnectorConsentBypassInputSchema = z
  .object({
    ...agentInputShape,
    adminConsentBypass: z.boolean(),
    confirmChange: z.boolean().optional(),
  })
  .strict();

export const reassignCopilotAgentOwnerInputSchema = z
  .object({
    ...agentInputShape,
    newOwnerAadUserId: guidSchema("newOwnerAadUserId"),
    confirmReassign: z.boolean().optional(),
  })
  .strict();

const guidProperty = (description: string) => ({
  type: "string" as const,
  pattern: GUID_JSON_PATTERN,
  description,
});

const environmentProperty = {
  type: "string" as const,
  minLength: 1,
  maxLength: 128,
  description: "Environment GUID/name. Defaults to the session's current environment.",
};

const agentProperties = {
  agentId: guidProperty("Copilot Studio agent (Dataverse bot) GUID."),
  environment: environmentProperty,
};

const ADMIN_REQUIREMENT =
  `Requires delegated Power Platform API ${COPILOT_ADMIN_PERMISSION} with admin consent ` +
  "and Power Platform Administrator, AI Administrator, or Global Administrator.";

export const getCopilotAgentQuarantineTool: Tool = {
  name: "get_copilot_agent_quarantine",
  description:
    "Read whether a Copilot Studio agent is quarantined through the official Power Platform " +
    `2024-10-01 API. Quarantined agents remain visible to makers and can be tested, but users cannot interact with them through other channels. ${ADMIN_REQUIREMENT}`,
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: agentProperties,
    required: ["agentId"],
  },
};

export const setCopilotAgentQuarantineTool: Tool = {
  name: "set_copilot_agent_quarantine",
  description:
    "Quarantine or unquarantine a Copilot Studio agent through the official Power Platform " +
    "2024-10-01 API. Set the explicit desired quarantined state; confirmChange=true is required. " +
    `Quarantine blocks interaction outside Copilot Studio maker testing. ${ADMIN_REQUIREMENT}`,
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      quarantined: {
        type: "boolean",
        description: "Explicit desired state: true to quarantine, false to unquarantine.",
      },
      confirmChange: {
        type: "boolean",
        description: "Set true to confirm this governance state change.",
      },
    },
    required: ["agentId", "quarantined"],
  },
};

export const getCopilotAgentConnectorConsentBypassTool: Tool = {
  name: "get_copilot_agent_connector_consent_bypass",
  description:
    "Read whether an administrator has bypassed connector consent cards for a Copilot Studio " +
    `agent through the official Power Platform 2024-10-01 API. ${ADMIN_REQUIREMENT}`,
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: agentProperties,
    required: ["agentId"],
  },
};

export const setCopilotAgentConnectorConsentBypassTool: Tool = {
  name: "set_copilot_agent_connector_consent_bypass",
  description:
    "SECURITY-SENSITIVE: enable or disable administrator bypass of connector consent cards for " +
    "one Copilot Studio agent through the official Power Platform 2024-10-01 API. When enabled, " +
    "users are not shown those consent cards before the standard-harness agent uses connectors on " +
    "their behalf; this does not apply to the GitHub Copilot harness. Set the explicit desired " +
    `adminConsentBypass state and confirmChange=true. ${ADMIN_REQUIREMENT}`,
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      adminConsentBypass: {
        type: "boolean",
        description:
          "Explicit desired state: true suppresses connector consent cards; false restores them.",
      },
      confirmChange: {
        type: "boolean",
        description: "Set true to confirm this security-sensitive consent change.",
      },
    },
    required: ["agentId", "adminConsentBypass"],
  },
};

export const reassignCopilotAgentOwnerTool: Tool = {
  name: "reassign_copilot_agent_owner",
  description:
    "Preview or reassign a Copilot Studio agent to a new Entra user through the official Power " +
    "Platform 2024-10-01 API. The new owner must belong to the tenant and meet licensing/environment " +
    "requirements. The previous owner loses access to the agent; other co-owners are unaffected. " +
    `Nothing changes unless confirmReassign=true. ${ADMIN_REQUIREMENT}`,
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      newOwnerAadUserId: guidProperty("Microsoft Entra object ID of the new owner."),
      confirmReassign: {
        type: "boolean",
        description: "Set true to confirm ownership reassignment after reviewing the preview.",
      },
    },
    required: ["agentId", "newOwnerAadUserId"],
  },
};

export const copilotAdminTools: Tool[] = [
  getCopilotAgentQuarantineTool,
  setCopilotAgentQuarantineTool,
  getCopilotAgentConnectorConsentBypassTool,
  setCopilotAgentConnectorConsentBypassTool,
  reassignCopilotAgentOwnerTool,
];

export interface CopilotAdminRichResult {
  text: string;
  structured: Record<string, unknown>;
}

function targetEnvironment(environment: string | undefined): string {
  return environment ?? "the session's current environment";
}

function quarantineText(status: CopilotQuarantineStatus): string {
  return [
    `Copilot Studio agent ${status.agentId} is ${status.isQuarantined ? "QUARANTINED" : "not quarantined"}.`,
    `Environment: ${status.environmentId}`,
    `Last changed: ${status.lastUpdateTimeUtc ?? "not reported"}`,
    status.isQuarantined
      ? "Users cannot interact with it through normal channels; maker testing remains available."
      : "No quarantine restriction is reported by this API.",
  ].join("\n");
}

function consentBypassText(status: CopilotConnectorConsentBypassStatus): string {
  return [
    `Connector consent-card bypass is ${status.adminConsentBypass ? "ENABLED" : "disabled"} for agent ${status.agentId}.`,
    `Environment: ${status.environmentId}`,
    status.adminConsentBypass
      ? "Security warning: users are not shown connector consent cards for this standard-harness agent."
      : "Connector consent cards remain enabled.",
  ].join("\n");
}

export async function handleGetCopilotAgentQuarantine(
  api: CopilotAdminApi,
  input: unknown,
): Promise<CopilotAdminRichResult> {
  const parsed = getCopilotAgentQuarantineInputSchema.parse(input);
  const quarantine = await api.getQuarantineStatus(parsed.agentId, parsed.environment);
  return { text: quarantineText(quarantine), structured: { quarantine } };
}

export async function handleSetCopilotAgentQuarantine(
  api: CopilotAdminApi,
  input: unknown,
): Promise<CopilotAdminRichResult> {
  const parsed = setCopilotAgentQuarantineInputSchema.parse(input);
  if (!parsed.confirmChange) {
    throw new ConfirmationRequiredError(
      [
        `Quarantine change not confirmed for agent ${parsed.agentId}.`,
        `Environment: ${targetEnvironment(parsed.environment)}`,
        `Desired state: ${parsed.quarantined ? "QUARANTINED" : "not quarantined"}.`,
        parsed.quarantined
          ? "This blocks users from interacting with the agent outside maker testing."
          : "This removes the quarantine restriction and can restore channel access.",
        "Nothing has changed. Set confirmChange=true to apply this exact desired state.",
      ].join("\n"),
    );
  }
  const quarantine = await api.setQuarantineStatus(
    parsed.agentId,
    parsed.quarantined,
    parsed.environment,
  );
  return {
    text: `Quarantine state updated.\n${quarantineText(quarantine)}`,
    structured: { quarantine, changed: true },
  };
}

export async function handleGetCopilotAgentConnectorConsentBypass(
  api: CopilotAdminApi,
  input: unknown,
): Promise<CopilotAdminRichResult> {
  const parsed = getCopilotAgentConnectorConsentBypassInputSchema.parse(input);
  const connectorConsentBypass = await api.getConnectorConsentBypass(
    parsed.agentId,
    parsed.environment,
  );
  return {
    text: consentBypassText(connectorConsentBypass),
    structured: { connectorConsentBypass },
  };
}

export async function handleSetCopilotAgentConnectorConsentBypass(
  api: CopilotAdminApi,
  input: unknown,
): Promise<CopilotAdminRichResult> {
  const parsed = setCopilotAgentConnectorConsentBypassInputSchema.parse(input);
  if (!parsed.confirmChange) {
    throw new ConfirmationRequiredError(
      [
        "SECURITY-SENSITIVE connector consent change not confirmed.",
        `Agent: ${parsed.agentId}`,
        `Environment: ${targetEnvironment(parsed.environment)}`,
        `Desired adminConsentBypass: ${parsed.adminConsentBypass}.`,
        parsed.adminConsentBypass
          ? "Enabling bypass suppresses connector consent cards before this standard-harness agent uses connectors on users' behalf. Obtain security/admin approval first."
          : "Disabling bypass restores the normal connector consent-card prompts.",
        "The setting does not apply to the GitHub Copilot harness.",
        "Nothing has changed. Set confirmChange=true to apply this exact desired state.",
      ].join("\n"),
    );
  }
  const connectorConsentBypass = await api.setConnectorConsentBypass(
    parsed.agentId,
    parsed.adminConsentBypass,
    parsed.environment,
  );
  return {
    text: `Connector consent-card setting updated.\n${consentBypassText(connectorConsentBypass)}`,
    structured: { connectorConsentBypass, changed: true },
  };
}

export async function handleReassignCopilotAgentOwner(
  api: CopilotAdminApi,
  input: unknown,
): Promise<CopilotAdminRichResult> {
  const parsed = reassignCopilotAgentOwnerInputSchema.parse(input);
  if (!parsed.confirmReassign) {
    throw new ConfirmationRequiredError(
      [
        `Ownership reassignment preview for Copilot Studio agent ${parsed.agentId}.`,
        `Environment: ${targetEnvironment(parsed.environment)}`,
        `New owner Entra user ID: ${parsed.newOwnerAadUserId}`,
        "The previous owner will lose access to the agent. Other co-owners are unaffected.",
        "The new owner must belong to the tenant and meet Copilot Studio licensing and environment requirements.",
        "Nothing has changed. Set confirmReassign=true to perform this reassignment.",
      ].join("\n"),
    );
  }
  const reassignment = await api.reassignOwner(
    parsed.agentId,
    parsed.newOwnerAadUserId,
    parsed.environment,
  );
  return reassignmentResult(reassignment);
}

function reassignmentResult(reassignment: CopilotOwnerReassignment): CopilotAdminRichResult {
  return {
    text: [
      `Reassigned Copilot Studio agent ${reassignment.agentId}.`,
      `Environment: ${reassignment.environmentId}`,
      `New owner Entra user ID: ${reassignment.newOwnerAadUserId}`,
      "The previous owner has lost access to the agent; other co-owners are unaffected.",
    ].join("\n"),
    structured: { reassignment },
  };
}
